import { useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'expo-router';
import { SOCKET_EVENTS } from '@tabbio/types';
import type { NewMessageEvent } from '@tabbio/types';
import { useSocket } from './useSocket';
import { useToast } from './useToast';
import { useAuth } from './useAuth';

/**
 * Global hook that shows in-app toast notifications for incoming messages.
 * Tapping the toast navigates to the conversation.
 * Mount this once in the root layout.
 */
export function useMessageNotifications() {
  const { socket } = useSocket();
  const toast = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';

  // Track which conversation the user is currently viewing
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (!socket || !currentUserId) return;

    const handleNewMessage = (event: NewMessageEvent) => {
      // Don't show toast for own messages
      if (event.message.senderId === currentUserId) return;

      // Muted conversations still receive and render messages in real time,
      // but must not surface recipient-facing in-app notifications.
      if (event.notificationsMuted) return;

      // Don't show toast if user is already viewing this conversation
      const currentPath = pathnameRef.current;
      if (currentPath.includes(`/messages/${event.conversationId}`)) return;

      const senderName = event.senderName?.trim();
      const preview = event.message.content?.slice(0, 80) ?? '';
      const title = senderName ? `New message from ${senderName}` : 'New message';

      toast.show({
        title,
        message: preview || undefined,
        type: 'info',
      });
    };

    socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);

    return () => {
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    };
  }, [socket, currentUserId, toast, router]);
}
